import React, { useState, useMemo } from 'react'

import { LinkOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Button, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import ListPageHeader from '@shared/ListPageHeader'
import TableIndex from '@shared/table/TableIndex'

import { merchantService } from '@services/operator/merchantService'
import { formatDateTime } from '../../../utils/dateTime'

interface MerchantRow {
  id: string;
  name?: string;
  activated?: boolean;
  created_at?: string;
  [key: string]: unknown;
}

export default function MerchantsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo<unknown[]>(() => [
    {
      nameKey: `operator.merchants.list.columns.name`,
      persist: true,
      sortable: true,
      sortFieldName: 'merchants.name',
      width: '200px',
      wrap: true,
      cell: ({ id, name }: MerchantRow) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Link to={`/merchants/${id}`} style={{ wordBreak: 'break-word' }}>
            {name}
          </Link>
          <a
            href={`/operator/merchants/${id}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} rel="noreferrer"
          >
            <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
          </a>
        </div>
      ),
    },
    {
      nameKey: `operator.merchants.list.columns.activated`,
      width: '120px',
      cell: ({ activated }: MerchantRow) => (
        <Tag color={activated ? 'success' : 'default'} icon={activated ? <CheckOutlined /> : <CloseOutlined />}>
          {activated ? t('common.status.active') : t('common.status.inactive')}
        </Tag>
      )
    },
    {
      nameKey: `common.created_at`,
      sortable: true,
      sortFieldName: 'merchants.created_at',
      width: '180px',
      wrap: true,
      cell: ({ created_at }: MerchantRow) => created_at ? formatDateTime(created_at) : '-',
    },
  ], [t])

  return (
    <div>
      <ListPageHeader
        items={['merchant_settings', 'merchant_settings.merchants']}
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/merchants/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        service={merchantService}
        searchable={{
          match: 'contains',
          searchFields: [t('operator.merchants.list.columns.name')],
        }}
        columnsEditable={true}
        tableId="OperatorMerchantsTable"
        defaultQuery={{
          sorting_field: 'merchants.id',
          sorting_direction: 'desc',
        }}
        supportRules={[]}
        lastModified={lastModified}
        setLastModified={setLastModified}
        actions={[]}
      />
    </div>
  )
}
